import { Button, DialogActions, DialogTitle, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useCallback, useEffect, useState } from 'react';
import useAuth from 'src/auth/useAuth';
import { Iconify } from '../iconify';

/**  Delay in milliseconds before cleaning up the temporary link and URL object after initiating the download  */
const CLEANUP_DELAY_MS = 100;

/**
 * @description Utility function to save an array of strings as a .txt file and trigger a download in the browser.
 */
const saveTextToTxtFile = (
  messages: string[],
  filename?: string,
  onSuccess?: VoidFunction
): void => {
  if (!messages || messages.length === 0) {
    console.warn('saveTextToTxtFile: No messages to download.');
    return;
  }

  let url: string | null = null;
  let link: HTMLAnchorElement | null = null;

  try {
    const fileContent = messages.join('\n');
    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });

    url = URL.createObjectURL(blob);

    link = document.createElement('a');
    link.href = url;
    link.download = filename ?? 'downloaded-file.txt';

    document.body.appendChild(link);
    link.click();
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    console.error('saveTextToTxtFile: Failed to download file.', error);
  } finally {
    // Defer cleanup to ensure the browser has time to start the download
    setTimeout(() => {
      if (link && document.body.contains(link)) {
        document.body.removeChild(link);
      }
      if (url) {
        URL.revokeObjectURL(url);
      }
    }, CLEANUP_DELAY_MS);
  }
};

/**
 * @description Dialog component that prompts the user to download their 2FA recovery codes for backup.
 * It prevents accidental navigation away from the page until the codes are downloaded or dismissed.
 */
export default function DialogTwoFactorRecoveryCodeDownload() {
  const { recoveryCodes, clearRecoveryCodes } = useAuth();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true);

  const shouldOpen = recoveryCodes.length > 0;

  const onDownloadClick = useCallback(() => {
    const timestamp = new Date().toISOString().replace(/:/g, '-');

    saveTextToTxtFile(recoveryCodes, `cheddar-admin-2fa-recovery-codes-${timestamp}.txt`, () => {
      setHasUnsavedChanges(false);
      clearRecoveryCodes();
    });
  }, [recoveryCodes, clearRecoveryCodes]);

  /**
   *  Effect to handle the beforeunload event, prompting the user if they try to navigate away with unsaved changes
   * (i.e., before downloading or dismissing the recovery codes).
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        // Deprecated but still needed for older browsers, e.g. Firefox < 44.
        event.returnValue = '';
      }
    };

    if (shouldOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, shouldOpen]);

  return (
    <Dialog
      aria-labelledby="2fa-dialog-title"
      open={shouldOpen}
      sx={{
        '& .MuiDialog-paper': {
          width: 'clamp(300px, 50%, 500px)',
        },
      }}
    >
      <DialogTitle
        id="2fa-dialog-title"
        sx={{
          '&.MuiTypography-root': {
            fontSize: '1.5em',
          },
        }}
      >
        Store recovery codes safely
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.2em',
            textAlign: 'center',
            fontWeight: '500',
          }}
        >
          Use these to access your account if you lose your 2FA device.
        </Typography>
        <Iconify
          icon="line-md:downloading-loop"
          width={200}
          style={{
            fontSize: '4rem',
            display: 'block',
            margin: '1rem auto',
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={clearRecoveryCodes} sx={{ color: 'text.secondary' }}>
          Dismiss
        </Button>
        <Button autoFocus variant="contained" onClick={onDownloadClick}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
